// ============================================
// DOMAIN LAYER - DTOs y Comandos
// ============================================

import type {
  MetodoPago,
  EstadoVenta
} from './entities';

// ---------- AUTH DTOs ----------
export interface LoginDto {
  almacenId: number;
  usuarioId: number;
  password: string;
}

export interface RegisterDto {
  nombre: string;
  email: string;
  password: string;
  almacenNombre: string;
}

// ---------- PRODUCTO DTOs ----------
export interface CreateProductoDto {
  codigo: string;
  nombre: string;
  descripcion?: string;
  categoriaId?: string;
  precioVenta: number;
  precioCompra: number;
  stock: number;
  stockMinimo: number;
  unidadMedida: 'UNIDAD' | 'CAJA' | 'PAQUETE' | 'KILO' | 'LITRO';
  imagen?: string;
}

export interface UpdateProductoDto extends Partial<CreateProductoDto> {
  activo?: boolean;
}

export interface ProductoFiltros {
  search?: string;
  categoriaId?: string;
  activo?: boolean;
  stockBajo?: boolean;
  page?: number;
  limit?: number;
}

export interface AddInventarioDto {
  productoAlmacenId: number;
  stock: number;
  precioCompra: number;
  precioVenta: number;
  fechaVencimiento: string | null;
}

export interface UpdateInventarioDto {
  stock: number;
  precioCompra: number;
  precioVenta: number;
  fechaVencimiento: string | null;
}

export interface MermaDto {
  productoAlmacenId: number;
  cantidad: number;
}

export interface UpdateProductoAlmacenDto {
  codigoProducto?: string;
  nombreProducto?: string;
  precioCompraActual?: number;
  precioVentaActual?: number;
  venderPor?: 'U' | 'F';
  imagenProductoId?: number;
}

// ---------- CLIENTE DTOs ----------
export interface CreateClienteDto {
  tipoDocumento: 'DNI' | 'RUC' | 'CE' | 'PASAPORTE';
  numeroDocumento: string;
  nombre: string;
  apellidos?: string;
  razonSocial?: string;
  telefono?: string;
  email?: string;
  direccion?: string;
}

export interface UpdateClienteDto extends Partial<CreateClienteDto> {
  activo?: boolean;
}

// ---------- VENTA DTOs ----------
export interface CreateVentaDto {
  clienteId?: string;
  detalles: Array<{
    productoId: string;
    cantidad: number;
    precioUnitario: number;
  }>;
  descuento?: number;
  metodoPago: MetodoPago;
  notas?: string;
}

export interface VentaFiltros {
  fechaInicio?: string;
  fechaFin?: string;
  clienteId?: string;
  metodoPago?: MetodoPago;
  estado?: EstadoVenta;
  page?: number;
  limit?: number;
}

// ---------- DASHBOARD DTOs ----------
export interface DashboardFiltros {
  fechaInicio?: string;
  fechaFin?: string;
}

// ---------- CAJA DTOs ----------
export interface AperturaCajaDto {
  usuarioId: number;
  fechaInicio: string; // YYYY-MM-DD
  horaInicio: string; // HH:mm:ss
  montoInicial: number;
}

export interface CierreCajaDto {
  cuadraturaCajaId: number;
  fechaCierre: string;
  horaCierre: string;
  montoFinal: number;
}

export interface AperturaCajaResponse {
  cuadraturaCajaId: number;
  usuarioId: number;
  fechaInicio: string;
  horaInicio: string;
  montoInicial: number;
  estado: string;
}

export interface VendedorCaja {
  id: number;
  nombreMostrable: string;
}

export interface CuadraturaCaja {
  id: number;
  usuarioId: number;
  cajero: string;
  fechaRegistro: string;
  fechaInicio: string;
  fechaCierre: string | null;
  horaInicio: string;
  horaCierre: string | null;
  montoInicial: number;
  ventasEfectivo: number | null;
  ventasDebito: number | null;
  ventasCredito: number | null;
  ventasFiado: number | null;
  ventasTransferencia: number | null;
  otrosIngresos: number | null;
  estado: 'OPEN' | 'CLOSED';
}

export interface CuadraturaCajaResponse {
  content: CuadraturaCaja[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    offset: number;
  };
  totalPages: number;
  totalElements: number;
  last: boolean;
  first: boolean;
  empty: boolean;
}

export interface FiltrosCuadraturaCaja {
  fechaInicio: string; // DD-MM-YYYY
  fechaFin: string; // DD-MM-YYYY
  page?: number;
}

export interface CajaStatusData {
  id: number;
  fechaRegistro: string;
  fechaInicio: string;
  horaInicio: string;
  montoInicial: number;
  ventasEfectivo: number;
  ventasDebito: number;
  ventasCredito: number;
  ventasFiado: number;
  ventasTransferencia: number;
  otrosIngresos: number;
}

export interface CajaStatusResponse {
  message: string;
  data: CajaStatusData | null;
  open: boolean;
}

// ---------- CAJA 360 (turnos) DTOs ----------
// Módulo nuevo, /api/caja/turnos - /api/caja/movimientos - /api/caja/configuracion.
// Coexiste con el bloque CAJA (viejo, /cuadratura-cajas) de arriba: no lo reemplaza todavía.

export type TurnoCajaEstado = 'ABIERTO' | 'CERRADO' | 'CERRADO_FORZADO';

export type MovimientoCajaTipo = 'RETIRO' | 'GASTO' | 'INGRESO_EXTRA' | 'AJUSTE_ANULACION' | 'ABONO_FIADO';

export type MovimientoCajaEstado = 'PENDIENTE' | 'APROBADO' | 'RECHAZADO';

export interface TurnoCaja {
  id: number;
  almacenId: number;
  usuarioId: number;
  fechaHoraApertura: string;
  fechaHoraCierre: string | null;
  estado: TurnoCajaEstado;
  montoInicial: number;
  observacionApertura: string | null;
  totalVentas: number | null;
  /** Ingresos aprobados de todos los medios de pago (informativo). */
  totalIngresos: number | null;
  /** Parte de totalIngresos pagada en efectivo (la que suma al efectivo esperado). null si no viene calculada. */
  totalIngresosEfectivo?: number | null;
  totalEgresos: number | null;
  efectivoEsperado: number | null;
  efectivoContado: number | null;
  diferenciaEfectivo: number | null;
  arqueoRealizado: boolean;
  usuarioCierreId: number | null;
  motivoCierre: string | null;
  /** Ventas del turno agrupadas por medio de pago (clave = "EFECTIVO", "DEBITO", etc). */
  ventasPorMedioPago: Record<string, number>;
}

/**
 * Turno que el usuario tiene abierto en OTRA sucursal (distinta a la de la
 * sesión actual). Uso exclusivo: avisar en "Mi caja" para que no quede
 * invisible ni sin cerrar — nunca para operar sobre él directamente.
 */
export interface TurnoOtraSucursal {
  turnoId: number;
  almacenId: number;
  direccionAlmacen: string | null;
  fechaHoraApertura: string;
}

/** Respuesta de GET /caja/turnos/me: el turno de esta sucursal (o null) y, solo si no hay uno acá, los abiertos en otras. */
export interface MiTurnoResult {
  turno: TurnoCaja | null;
  turnosOtrasSucursales: TurnoOtraSucursal[];
}

export interface AbrirTurnoDto {
  montoInicial: number;
  observacion?: string;
  /** Opcional: hora real de apertura si se olvidó abrir la caja a tiempo ("YYYY-MM-DDTHH:mm"). */
  fechaHoraApertura?: string;
  /** Opcional: abrir la caja para otro usuario del almacén (solo propietario). */
  usuarioId?: number;
  /** Confirma abrir la caja aunque el almacén tenga un turno abierto hace más de 3 días (alerta del backend). */
  confirmado?: boolean;
}

export interface CerrarTurnoDto {
  efectivoContado?: number;
}

export interface MovimientoCaja {
  id: number;
  turnoCajaId: number;
  almacenId: number;
  tipo: MovimientoCajaTipo;
  medioPago: string;
  monto: number;
  glosa: string;
  estado: MovimientoCajaEstado;
  usuarioCreacionId: number;
  fechaHoraCreacion: string;
  usuarioAprobacionId: number | null;
  fechaHoraAprobacion: string | null;
  motivoRechazo: string | null;
}

export interface RegistrarMovimientoDto {
  tipo: 'RETIRO' | 'GASTO' | 'INGRESO_EXTRA';
  monto: number;
  glosa: string;
}

export interface RechazarMovimientoDto {
  motivo: string;
}

export interface TurnoListItem {
  id: number;
  usuarioId: number;
  usuarioNombre: string;
  fechaHoraApertura: string;
  fechaHoraCierre: string | null;
  estado: TurnoCajaEstado;
  montoInicial: number;
  efectivoContado: number | null;
  diferenciaEfectivo: number | null;
  arqueoRealizado: boolean;
}

export interface TurnoListResponse {
  content: TurnoListItem[];
  pageable: { pageNumber: number; pageSize: number; offset: number };
  totalPages: number;
  totalElements: number;
  last: boolean;
  first: boolean;
  empty: boolean;
}

export interface MovimientoListItem {
  id: number;
  turnoCajaId: number;
  tipo: MovimientoCajaTipo;
  medioPago: string;
  monto: number;
  glosa: string;
  estado: MovimientoCajaEstado;
  usuarioCreacionId: number;
  usuarioCreacionNombre: string;
  fechaHoraCreacion: string;
}

/** Item de la auditoría de "deshacer": qué resolución tenía el movimiento antes y quién la deshizo. */
export interface MovimientoDeshacerLogItem {
  id: number;
  movimientoCajaId: number;
  tipo: MovimientoCajaTipo;
  monto: number;
  glosa: string;
  usuarioCreacionId: number;
  usuarioCreacionNombre: string;
  estadoAnterior: 'APROBADO' | 'RECHAZADO';
  usuarioResolucionId: number;
  usuarioResolucionNombre: string;
  fechaHoraResolucion: string;
  motivoRechazoAnterior: string | null;
  usuarioDeshaceId: number;
  usuarioDeshaceNombre: string;
  fechaHoraDeshacer: string;
}

export interface MovimientoDeshacerLogListResponse {
  content: MovimientoDeshacerLogItem[];
  pageable: { pageNumber: number; pageSize: number; offset: number };
  totalPages: number;
  totalElements: number;
  last: boolean;
  first: boolean;
  empty: boolean;
}

export interface MovimientoListResponse {
  content: MovimientoListItem[];
  pageable: { pageNumber: number; pageSize: number; offset: number };
  totalPages: number;
  totalElements: number;
  last: boolean;
  first: boolean;
  empty: boolean;
}

export interface ConfiguracionCaja {
  almacenId: number;
  arqueoObligatorio: boolean;
  aprobacionMovimientos: boolean;
}

export interface ActualizarConfiguracionCajaDto {
  arqueoObligatorio: boolean;
  aprobacionMovimientos: boolean;
}

export interface FiltrosTurnos {
  usuarioId?: number;
  desde?: string; // YYYY-MM-DD
  hasta?: string; // YYYY-MM-DD
  estado?: TurnoCajaEstado;
  page?: number;
  sort?: string; // "campo,asc" | "campo,desc"
}

export interface ForzarCierreTurnoDto {
  motivo: string;
  efectivoContado?: number;
}

// ---------- TUTORIALES DTOs ----------
export interface ModuloTutorial {
  moduleName: string;
}

export interface TutorialStep {
  id: number;
  title: string;
  videoLink: string;
  description: string;
  order: number;
  platform: 'VIMEO' | 'YOUTUBE';
}

export interface TutorialModulo {
  moduleName: string;
  moduleDescription: string;
  steps: TutorialStep[];
}

// ---------- BOLETAS DTOs ----------

export enum TipoPago {
  EFECTIVO = 'EFECTIVO',
  DEBITO = 'DEBITO',
  CREDITO = 'CREDITO',
  FIADO = 'FIADO',
  TRANSFERENCIA = 'TRANSFERENCIA',
}

export interface Boleta {
  id: number;
  total: number;
  tipoPago: string;
  fecha: string; // ISO 8601 format
  vendedor: string;
  anulada: boolean;
  dteEmitido: boolean;
  siiDteEmitidoFolio: number | null;
  detalles: any | null;
}

export interface DetalleBoletaProducto {
  id: number;
  nombreProducto: string;
  cantidad: number;
  precioVenta: number;
  total: number;
  /** Descuento de la línea en CLP; el total ya viene neto de este monto. */
  descuento?: number;
  esPack: boolean;
}

export interface BoletaPago {
  methodPayment: string; // 'EFECTIVO' | 'DEBITO' | 'CREDITO' | 'FIADO' | 'TRANSFERENCIA' | 'COMPRAAQUI' | 'PEDIDOSYA'
  amount: number;
  fiadoClienteId: number | null;
  orden: number;
}

export interface BoletaDetalle {
  id: number;
  total: number;
  tipoPago: string;
  fecha: string; // ISO 8601 format
  vendedor: string;
  anulada: boolean;
  dteEmitido: boolean;
  siiDteEmitidoFolio: number | null;
  detalles: DetalleBoletaProducto[];
  payments?: BoletaPago[]; // solo boletas multipago; el endpoint la trae aunque el DTO no la declarara
}

export interface FiltrosBoletasDto {
  desde: string; // YYYY-MM-DD
  hasta: string; // YYYY-MM-DD
}

export interface DetalleBoletaActualizarDto {
  detalleId: number;
  cantidad: number;
  eliminado: boolean;
}

export interface ActualizarBoletaDto {
  tipoPago: string;
  detalles: DetalleBoletaActualizarDto[];
}

// ---------- SUCURSALES DTOs ----------
export interface Sucursal {
  id: number;
  nombre: string;
  direccion: string;
  numeroDireccion: number;
  comuna: string;
  fechaCreacion: string; // YYYY-MM-DD
  rut: string;
  dv: string;
  activo: boolean;
}

export interface CrearSucursalDto {
  nombre: string;
  direccion: string;
  numeroDireccion: number;
  comunaId: number;
}

export interface ActualizarSucursalDto {
  nombre: string;
  direccion: string;
  numeroDireccion: number;
}

// ---------- USUARIOS DTOs ----------
export interface UsuarioSucursal {
  id: number;
  nombre: string;
  apellidos: string | null;
  run: number | null;
  dv: string | null;
  correo: string | null;
  esPropietario: boolean;
  activo: boolean;
  sucursalId: number | null;
}

export interface CrearUsuarioDto {
  nombre: string;
  apellidos: string;
  sucursalId: number;
  password: string;
}

export interface ActualizarUsuarioDto {
  usuarioId: number;
  nombre: string;
  apellidos: string;
  sucursalId: number | null;
  password: string;
}

export interface SucursalUsuario {
  id: number;
  nombre: string;
}

export interface UsuarioDetalle {
  id: number;
  nombre: string;
  apellidos: string | null;
  run: number | null;
  dv: string | null;
  correo: string | null;
  nombreUsuario: string | null;
  sucursales: SucursalUsuario[];
  esPropietario: boolean;
  activo: boolean;
}

// ---------- PERFIL ALMACÉN DTOs ----------
export interface PerfilAlmacen {
  id: number;
  runPropietario: number;
  nombre: string;
  rut: number;
  direccion: string;
  numeroDireccion: number;
  local: string | null;
  fechaCreacion: string; // YYYY-MM-DD
  activo: boolean;
  comuna: {
    id: number;
    nombre: string;
    region: {
      codigo: number;
      nombre: string;
      ordenGeografico: number;
      cmExcluir: boolean;
    };
  };
  tienePatenteAlcohol: boolean;
  tieneFrios: boolean;
  tieneCongelados: boolean;
  tamanoLocal: string | null;
  vendePorMayor: boolean;
  medioInformacion: string | null;
  tipoAlmacen: string | null;
  siiPassword: string | null;
  updatingStatusRcv: number;
  siiPasswordCorrecta: boolean;
  tipoAlmacenValido: boolean;
}

// ---------- LOGO ALMACÉN DTOs ----------
export interface LogoAlmacen {
  imagenAlmacenId: number;
  rut: number;
  url: string;
  contentType: string;
  ancho: number;
  alto: number;
  tamanoBytes: number;
  colorFondo: string;
  fechaCreacion: string; // ISO 8601
}

// ---------- ECOMMERCE DTOs ----------
export interface DetallePedido {
  detalleId: number;
  productoAlmacenId: number;
  nombreProducto: string;
  cantidad: number;
  precioVentaActual: number;
  total: number;
}

export interface Pedido {
  pedidoId: number;
  nombreCliente: string;
  telefonoCliente: string;
  estado: 'PENDIENTE' | 'CANCELADO' | 'COMPLETADO';
  fechaHoraPickup: string;
  total: number;
  boletaId: number | null;
  detalles: DetallePedido[];
}

export interface PedidosResponse {
  content: Pedido[];
  paginaActual: number;
  totalPaginas: number;
  totalElementos: number;
  tamanoPagina: number;
}

export interface PedidosFiltros {
  cliente?: string;
  estado?: 'PENDIENTE' | 'CANCELADO' | 'COMPLETADO';
  desde?: string;
  hasta?: string;
  page?: number;
}

// ---------- FIADOS ----------
export interface ClienteFiadoPendiente {
  clienteAlmacenId: number;
  clienteId: number;
  clienteNombre: string;
  clienteTelefono: string;
  clienteRut: string | null;
  clienteDv: string | null;
  saldoPendiente: number;
  aliasNombre?: string | null;
  ultimaFechaFiado: string;
}

export interface FiadoHistorico {
  fiadoId: number;
  clienteAlmacenId: number;
  clienteId: number;
  boletaId: number;
  clienteNombre: string;
  clienteTelefono: string;
  clienteRut: string | null;
  clienteDv: string | null;
  fecha: string;
  pagado: boolean;
  montoAbonado: number;
  montoTotal: number;
  saldoPendiente: number;
  aliasNombre?: string | null;
}

export interface FiadosPendientesResponse {
  content: ClienteFiadoPendiente[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
  totalPages: number;
  totalElements: number;
  last: boolean;
  size: number;
  number: number;
  numberOfElements: number;
  first: boolean;
  empty: boolean;
}

export interface FiadosHistoricoResponse {
  content: FiadoHistorico[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
  totalPages: number;
  totalElements: number;
  last: boolean;
  size: number;
  number: number;
  numberOfElements: number;
  first: boolean;
  empty: boolean;
}

export interface FiltrosFiadosPendientes {
  nombreCliente?: string;
  rutDv?: string;
  page?: number;
  sort?: string;
}

export interface FiltrosFiadosHistorico {
  nombreCliente?: string;
  pagado?: boolean;
  fechaDesde: string;
  fechaHasta: string;
  page?: number;
}

export interface PagoFiadoDto {
  idFiado: number;
  tipoPago: 'EFECTIVO' | 'DEBITO' | 'CREDITO' | 'TRANSFERENCIA';
  monto: number;
}

export interface PagarFiadoRequest {
  pagos: PagoFiadoDto[];
}

export interface DetalleFiadoCliente {
  id: number;
  nombreCliente: string;
  telefonoCliente: string;
  fechaHoraTransaccion: string;
  pagado: boolean;
  monto: number;
  abonoFiado: number;
  saldoPendiente: number;
  boletaId: number;
  aliasNombre?: string | null;
}

export interface ReasignarFiadoRequest {
  clienteAlmacenId: number;
}

export interface DetalleFiadosClienteResponse {
  content: DetalleFiadoCliente[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
  totalPages: number;
  totalElements: number;
  last: boolean;
  size: number;
  number: number;
  numberOfElements: number;
  first: boolean;
  empty: boolean;
}

export interface FiadoTotalesResponse {
  totalFiados: number;
  saldoPendienteTotal: number;
  totalAbonado: number;
}

export interface Abono {
  id: number;
  fiadoId: number;
  fechaPago: string;
  monto: number;
  tipoPago: string;
}

// ---------- BUSQUEDA Y ACTUALIZACION DE CLIENTES (FIADOS) ----------
export interface BuscarClienteDto {
  nombre?: string;
  telefono?: string;
  rutDv?: string;
}

export interface ClienteFiadoResult {
  clienteId: number;
  nombre: string;
  rut: number;
  dv: string;
  telefono: string;
  permiteEditar: boolean;
  clienteAlmacenId?: number | null;
  alias?: string | null;
}

export interface ActualizarClienteFiadoDto {
  clienteId: number;
  nombre: string;
  telefono: string;
  rutDv: string;
}

export interface CrearClienteFiadoDto {
  nombre: string;
  telefono: string;
  rutDv: string;
}

// ---------- VENTAS ----------
export interface ProductoVentaDto {
  productoAlmacenId: number | null;
  nombre: string;
  discount: number;
  quantity: number;
  total: number;
  salePrice?: number; // Solo para productos de calculadora
  sellBy?: 'U' | 'F'; // Solo para productos de calculadora
  packId?: number; // productoAlmacenId del pack padre, si esta línea viene de expandir un pack
  packQuantity?: number; // cantidad de packs vendidos en el carrito
}

export interface CrearVentaPosDto {
  methodPayment: 'EFECTIVO' | 'DEBITO' | 'CREDITO' | 'TRANSFERENCIA' | 'FIADO';
  fiadoClienteId?: number; // Solo si methodPayment es FIADO
  billingType: null | 39; // null: Voucher, 39: Boleta electrónica SII
  finalPrice: number;
  products: ProductoVentaDto[];
}

// Multi-Payment
export interface PaymentDto {
  methodPayment: 'EFECTIVO' | 'DEBITO' | 'CREDITO' | 'TRANSFERENCIA' | 'FIADO' | 'COMPRAAQUI' | 'PEDIDOSYA';
  amount: number;
  fiadoClienteId?: number; // Solo si methodPayment es FIADO
}

export interface CrearVentaMultiPagoDto {
  products: ProductoVentaDto[];
  payments: PaymentDto[];
  billingType: null | 39; // null: Voucher, 39: Boleta electrónica SII
  finalPrice: number;
}

export interface VentaItemResponse {
  productoAlmacenId: number | null;
  nombreProducto: string;
  totalSaleAmount: number;
  quentity: number;
  stockCurrent: number;
}

export interface CrearVentaPosResponse {
  boletaId: number;
  folio: number | null;
  montoTotal: number;
  items: VentaItemResponse[];
}

export interface VentaErrorField {
  field: string;
  message: string;
}

export interface VentaErrorResponse {
  code: string;
  message: string;
  timestamp: string;
  fieldErrors?: VentaErrorField[];
}

// ---------- SII / DTE ----------

export interface CertificadoSiiStatus {
  hasCertificate: boolean;
  isValid: boolean;
  isPasswordCorrect: boolean;
  expirationDate: string; // Formato: "2027-03-27"
  daysRemaining: number;
}

export interface FolioSiiActivo {
  tipoDocumento: number;
  nombreDocumento: string;
  foliosTotales: number;
  foliosDisponibles: number;
  porcentajeUso: number;
  fechaVencimiento: string; // Formato: "2026-10-02"
  diasRestantes: number;
  alertaCritica: boolean;
}

export interface TimbrajeResult {
  folioInicial: number;
  folioFinal: number;
  cantidad: number;
  fromCache: boolean;
}

export interface SiiErrorResponse {
  code: string;
  message: string;
  timestamp: string;
  fieldErrors: any[] | null;
}

// ============================================
// PERMISOS - Genérico, hoy solo con valores de caja
// ============================================

export type PermisoCaja =
  | 'CAJA_ABRIR_PARA_OTRO'
  | 'CAJA_CERRAR_FORZADO'
  | 'CAJA_APROBAR_MOVIMIENTOS'
  | 'CAJA_RECHAZAR_MOVIMIENTOS'
  | 'CAJA_DESHACER_RESOLUCION'
  | 'CAJA_VER_PENDIENTES'
  | 'CAJA_CONFIGURAR';

/**
 * PROPIO: le da al vendedor autonomía sobre su propia caja/turno.
 * ALMACEN: le da control sobre otros vendedores o sobre configuración que
 * afecta a todo el almacén.
 */
export type AlcancePermiso = 'PROPIO' | 'ALMACEN';

export interface PermisoCatalogoItem {
  permiso: PermisoCaja;
  alcance: AlcancePermiso;
  etiqueta: string;
  descripcion: string;
  moduloCodigo: string;
  moduloNombre: string;
}
